import clsx from "clsx";
import { get } from "lodash";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import filterFactory, { FILTER_TYPES } from "react-bootstrap-table2-filter";
import { FilterContext } from "./filters";
import * as formatters from "./formatters";
import NoDataIndication from "./NoDataIndication";
import "./Table.scss";
import TableHeader from "./TableHeader";
import useConfiguredColumns from "./useConfiguredColumns";
import useGlobalSearch from "./useGlobalSearch";
import usePagination from "./usePagination";
import useSelectRow from "./useSelectRow";
import useSort from "./useSort";

const TABLE_LOADING_ROW_COUNT = 10;

const propTypes = {
  /**
   * Especify which parameter will work as a list key
   */
  keyField: PropTypes.string.isRequired,
  /**
   * Data provided to the table
   */
  data: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  /**
   * Table columns
   */
  columns: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  /**
   * Error loading the table.
   * Note: data has to be empty in order to show the error
   */
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.bool,
  ]),
  /**
   * If true, table will not be paginated and will display all data passed to it
   */
  hidePagination: PropTypes.bool,
  /**
   * If enabled, you are supposed to be handle all the table change events
   */
  remote: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      filter: PropTypes.bool,
      pagination: PropTypes.bool,
      sort: PropTypes.bool,
      cellEdit: PropTypes.bool,
    }),
  ]),
  /**
   * Called when remote enabled only and pass the following arguments: type, newState.
   * More on: https://react-bootstrap-table.github.io/react-bootstrap-table2/docs/table-props.html#ontablechange-function
   */
  onTableChange: PropTypes.func,
  /**
   * Total number of rows in the table. Necessary when pagination is REMOTE
   */
  totalSize: PropTypes.number,
  /**
   * Specify the current page when table render. Necessary when pagination is REMOTE
   */
  page: PropTypes.number,
  /**
   *  Specify the current size per page when table render. Necessary when pagination is REMOTE
   */
  sizePerPage: PropTypes.number,
  /**
   * Object that controls the filters when table is REMOTE
   */
  filters: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  /**
   * Callback to get table controls, called with param tableControls={ filter: { clearAll, clear, update } }
   */
  getTableControls: PropTypes.func,
  /**
   * Callback that will be called when rows are selected/unselected and pass the following arguments: selectedRows
   * When enabled, table will show the selectable checkboxes
   * Note: this is a different callback from react-bootstrap documentation
   */
  onSelect: PropTypes.func,
  /**
   * Controls which rows are selected externally (full selected data)
   */
  selected: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  /**
   * Allow you to restrict some rows which can not be selected by user. Accept array with the rows keys
   * https://react-bootstrap-table.github.io/react-bootstrap-table2/docs/row-select-props.html#selectrownonselectable-array
   */
  nonSelectable: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  /**
   * Called when 'Select All Pages' button is clicked, it does NOT pass any arguments.
   * It's responsibility of the developer to set 'selected' to all data when the button is clicked.
   * 'Select All Pages' button will only appear if this callback is passed.
   */
  onSelectAllPages: PropTypes.func,
  /**
   * Indicates that the 'select all pages' button is loading
   */
  loadingSelectAllPages: PropTypes.bool,
  /**
   * Callback to format total size message and pass the following arguments: totalSize
   */
  formatTotalSizeMessage: PropTypes.func,
  /**
   * Callback to format total size message and pass the following arguments: selectedSize
   */
  formatSelectedSizeMessage: PropTypes.func,
  /**
   * function to render components in the table header and pass the following arguments: selected (array of selected row ids)
   */
  headerActionsRenderer: PropTypes.func,
  /**
   * If true, hides table header
   */
  hideHeader: PropTypes.bool,
  /**
   * If true, hides table borders
   */
  borderless: PropTypes.bool,
  /**
   * If true, table will show a loading animation and have its controls disabled
   */
  loading: PropTypes.bool,
  /**
   * If true, menu and checkboxes selectors will be hidden
   */
  readOnly: PropTypes.bool,
  /**
   * Menu is an array composed of objects repreenting its option.
   * label is the menu label. If passed as a function will be called with the following arguments: row
   * onClick is a callback for when the option is clicked and pass the following arguments: row
   * display is a callback to show/hide the option and pass following arguments: row
   * isDisabled is a callback check if the option should be disabled or not and pass following arguments: row
   * disableLabel overlay of the label if option is disabled
   */
  menu: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
      onClick: PropTypes.func.isRequired,
      display: PropTypes.func,
      isDisabled: PropTypes.func,
      disableLabel: PropTypes.node,
    })
  ),
  /**
   * Dictates whether to show the menu for each row based on the row values, pass following arguments: row.
   */
  showMenu: PropTypes.func,
  /**
   * Callback called with the search value when the global 'Search' input on the top of the table changes.
   * Note: onGlobalSearchChange has to be memoized
   */
  onGlobalSearchChange: PropTypes.func,
  /**
   * Indicates the refresh click functionality
   */
  onRefreshClick: PropTypes.func,
  /**
   * should refresh button show
   */
  showRefresh: PropTypes.bool,
  /*
   * expand row feature
   */
  expandRow: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  /**
   * Function provided to the table to listen when the filter settings change
   */
  afterFilter: PropTypes.func,
  /**
   * List of objects with text and value properties.
   * Used to add additional pagination buttons past the defaults
   */
  additionalSizePerPageValues: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      value: PropTypes.number,
    })
  ),
  /**
   * Class name to be applied to the table container
   */
  className: PropTypes.string,
  rowEvents: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

/**
 * Table description in README file
 */
function Table(props) {
  const {
    keyField,
    data = [],
    columns,
    error = null,
    remote = false,
    totalSize = undefined,
    hidePagination = false,
    page = undefined,
    sizePerPage = undefined,
    onSelect = undefined,
    selected = undefined,
    nonSelectable = [],
    onSelectAllPages = undefined,
    loadingSelectAllPages = false,
    formatTotalSizeMessage = undefined,
    formatSelectedSizeMessage = undefined,
    headerActionsRenderer = undefined,
    loading = false,
    readOnly = false,
    menu = null,
    showMenu = undefined,
    onGlobalSearchChange = undefined,
    onTableChange = undefined,
    filters = undefined,
    getTableControls = null,
    onRefreshClick = () => {},
    showRefresh = false,
    expandRow = undefined,
    hideHeader = false,
    borderless = false,
    afterFilter = null,
    additionalSizePerPageValues = [],
    className = undefined,
    rowEvents,
    ...rest
  } = props ?? {};

  const isRemote = remote === true || get(remote, "pagination") === true;

  const searchable = useMemo(
    () =>
      columns
        .filter((column) => column.searchable)
        .map((column) => column.dataField),
    [columns]
  );

  const [rowCount, setRowCount] = useState(0);

  useEffect(() => {
    // the table row count is controlled by totalSize if REMOTE
    // and by data.length/onDataSizeChange if NOT remote
    setRowCount(totalSize || data.length);
  }, [totalSize, data.length]);

  const pagination = usePagination({
    rowCount,
    page,
    sizePerPage,
    isRemote,
    loading,
    additionalSizePerPageValues,
  });

  const sort = useSort({ loading });

  const selectRow = useSelectRow({
    keyField,
    externalOnSelect: onSelect,
    selected,
    nonSelectable,
    loading,
    readOnly,
  });

  const filtersOnChange = useMemo(() => ({}), []);

  const configuredColumns = useConfiguredColumns({
    columns,
    loading,
    readOnly,
    menu,
    showMenu,
    filtersOnChange,
  });

  const tableControls = useMemo(
    () => ({
      filter: {
        clearAll: () => {
          Object.values(filtersOnChange).forEach((onChange) => {
            onChange("");
          });
        },
        clear: (dataField) => {
          const onChange = filtersOnChange[dataField];
          if (onChange) {
            onChange("");
          }
        },
        update: (dataField, value) => {
          const onChange = filtersOnChange[dataField];
          if (onChange) {
            onChange(value);
          }
        },
      },
    }),
    [filtersOnChange]
  );

  useEffect(() => {
    if (getTableControls) {
      getTableControls(tableControls);
    }
  }, [tableControls]); // eslint-disable-line react-hooks/exhaustive-deps

  const { search, setSearch, searchedData } = useGlobalSearch({
    data,
    searchable,
    isRemote,
  });

  const showLoadingRows = loading && searchedData.length === 0;
  const displayData = useMemo(
    () =>
      showLoadingRows
        ? Array.from({ length: TABLE_LOADING_ROW_COUNT }, (_, index) => ({
            [keyField]: `loading-${index}`,
          }))
        : searchedData,
    [showLoadingRows, searchedData, keyField]
  );
  const tableRowEvents = loading ? undefined : rowEvents;

  // empty fields should return null onTableChange
  const internalOnTableChange = useCallback(
    (type, newState) => {
      const newFilters = { ...newState?.filters };

      columns?.forEach((column) => {
        if (column.filter && !newFilters[column.dataField]) {
          newFilters[column.dataField] = { filterVal: null };
        }
      });

      onTableChange(type, { ...newState, filters: newFilters });
    },
    [onTableChange, columns]
  );

  // sync react-bootstrap-table filter state with remote filter state (redux)
  const ref = React.createRef();
  useEffect(() => {
    const currFilters = ref?.current?.filterContext?.currFilters;
    if (isRemote && filters && currFilters) {
      columns?.forEach((column) => {
        if (column.filter) {
          currFilters[column.dataField] = {
            ...currFilters[column.dataField],
            ...filters?.[column.dataField],
          };
        }
      });
    }
  }, [filters, isRemote, ref, columns]);

  return (
    <FilterContext.Provider value={filters}>
      {!hideHeader && (
        <TableHeader
          formatTotalSizeMessage={formatTotalSizeMessage}
          formatSelectedSizeMessage={formatSelectedSizeMessage}
          rowCount={rowCount}
          selectedCount={selectRow?.selected?.length}
          onSelectAll={selectRow?.onSelectAll}
          headerActionsRenderer={headerActionsRenderer}
          loading={loading}
          searchable={searchable}
          search={search}
          setSearch={setSearch}
          onGlobalSearchChange={onGlobalSearchChange}
          onSelectAllPages={onSelectAllPages}
          loadingSelectAllPages={loadingSelectAllPages}
          onRefreshClick={onRefreshClick}
          showRefresh={showRefresh}
        />
      )}
      <div
        className={clsx(
          "Uic-Table-base",
          rowCount === 0 && !showLoadingRows && "Uic-Table-empty",
          loading && "Uic-Table-loading",
          borderless && "Uic-Table-borderless",
          className
        )}
      >
        <BootstrapTable
          bootstrap4
          ref={ref}
          bordered={false}
          keyField={keyField}
          data={displayData}
          columns={configuredColumns}
          remote={remote}
          pagination={!hidePagination ? pagination : null}
          sort={sort}
          noDataIndication={
            <NoDataIndication error={error} loading={loading} />
          }
          wrapperClasses="Uic-Table-wrapper"
          headerClasses="Uic-Table-header"
          rowClasses="Uic-Table-row"
          rowEvents={tableRowEvents}
          filter={filterFactory({ afterFilter })}
          onDataSizeChange={({ dataSize }) => {
            if (!showLoadingRows) {
              setRowCount(dataSize);
            }
          }}
          onTableChange={internalOnTableChange}
          selectRow={selectRow}
          expandRow={expandRow}
          {...rest}
        />
      </div>
    </FilterContext.Provider>
  );
}

Table.propTypes = propTypes;

// re-exporting table variables inside Table
Table.FILTER_TYPES = FILTER_TYPES;
Table.formatters = formatters;

export default Table;
